import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const TYPE_COLORS = { info:'#314F5C', warn:'#d97706', success:'#2da06a', urgent:'#e05c5c' };
const TYPE_BG     = { info:'#edf6fb', warn:'#fef3c7', success:'#f0fbf5', urgent:'#fef0ef' };
const BLANK = { id:null, title:'', body:'', type:'info', active:true, country_code:'', starts_at:'', ends_at:'' };

function NoticesBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [notices,  setNotices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState(BLANK);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);
  const [me,       setMe]       = useState('admin');

  function showToast(msg, type) { setToast({ msg, type:type||'ok' }); setTimeout(() => setToast(null), 3000); }

  const load = useCallback(async () => {
    try {
      const { data } = await supabase.rpc('admin_get_notices');
      setNotices(data || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.auth.getUser().then(({ data }) => { if (data?.user) setMe(data.user.email?.split('@')[0]||'admin'); }).catch(() => {});
  }, [load]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      await supabase.rpc('admin_upsert_notice', {
        p_id:          form.id || null,
        p_title:       form.title.trim(),
        p_body:        form.body.trim(),
        p_type:        form.type,
        p_active:      form.active,
        p_country_code:form.country_code.trim() || null,
        p_starts_at:   form.starts_at ? new Date(form.starts_at + 'T00:00:00').toISOString() : null,
        p_ends_at:     form.ends_at   ? new Date(form.ends_at + 'T00:00:00').toISOString()   : null,
        p_created_by:  me,
      });
      showToast(form.id ? 'Notice updated' : 'Notice created');
      setForm(BLANK);
      setEditing(false);
      await load();
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
    setSaving(false);
  }

  async function handleToggle(n) {
    try {
      await supabase.rpc('admin_upsert_notice', {
        p_id: n.id, p_title: n.title, p_body: n.body, p_type: n.type,
        p_active: !n.active, p_country_code: n.country_code||null,
        p_starts_at: n.starts_at||null, p_ends_at: n.ends_at||null, p_created_by: me,
      });
      setNotices(ns => ns.map(x => x.id===n.id ? {...x, active:!x.active} : x));
      showToast(n.active ? 'Notice deactivated' : 'Notice activated');
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
  }

  async function handleDelete(id) {
    try {
      await supabase.rpc('admin_delete_notice', { p_id: id });
      setNotices(ns => ns.filter(x => x.id !== id));
      showToast('Notice deleted', 'warn');
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
  }

  function editNotice(n) {
    setForm({ ...n, starts_at: n.starts_at ? n.starts_at.split('T')[0] : '', ends_at: n.ends_at ? n.ends_at.split('T')[0] : '' });
    setEditing(true);
  }

  const inp = (label, key, ph, type) => (
    <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:'.63rem', color:t.mut, marginBottom:3 }}>{label}</div>
      <input type={type||'text'} value={form[key]||''} onChange={e => set(key, e.target.value)} placeholder={ph}
        style={{ fontSize:'.72rem', border:'1px solid #d8eaf4', borderRadius:7, padding:'7px 10px', color:t.tx, outline:'none', fontFamily:F, width:'100%', boxSizing:'border-box' }} />
    </div>
  );

  return (
    <div style={{ fontFamily: F }}>
      {toast && <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background:toast.type==='warn'?'#7c3a00':'#1b5e20', color:'#fff', padding:'10px 20px', borderRadius:10, fontSize:'.78rem', fontWeight:500, zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>}

      <Insight type="info">
        App notices appear as banners in the Sanoire app. Active notices are returned by the <code style={{ background:'#edf6fb', padding:'1px 5px', borderRadius:4, fontSize:'.85em' }}>app_notices</code> table which the main app should query on load. Optionally target by country code (e.g. JP, SG).
      </Insight>

      {/* Create / edit form */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: editing?14:0 }}>
          <CardTitle>Create notice</CardTitle>
          <Btn variant={editing?'outline':'primary'} onClick={() => { setEditing(e=>!e); if(editing) setForm(BLANK); }}>
            {editing ? 'Cancel' : '+ New notice'}
          </Btn>
        </div>
        {editing && (
          <>
            {inp('Title', 'title', 'e.g. We just added 10,000 locations in Spain')}
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:'.63rem', color:t.mut, marginBottom:3 }}>Body</div>
              <textarea value={form.body} onChange={e => set('body', e.target.value)} placeholder="Full notice text shown to users…" rows={3}
                style={{ fontSize:'.72rem', border:'1px solid #d8eaf4', borderRadius:7, padding:'7px 10px', color:t.tx, outline:'none', fontFamily:F, width:'100%', resize:'vertical', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8, marginBottom:10 }}>
              <div>
                <div style={{ fontSize:'.63rem', color:t.mut, marginBottom:3 }}>Type</div>
                <select value={form.type} onChange={e => set('type', e.target.value)}
                  style={{ fontSize:'.72rem', border:'1px solid #d8eaf4', borderRadius:7, padding:'7px 10px', color:t.tx, fontFamily:F, width:'100%' }}>
                  {['info','warn','success','urgent'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
              </div>
              {inp('Country code (optional)', 'country_code', 'e.g. JP or leave empty')}
              <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:20 }}>
                <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ width:14, height:14, accentColor:'#314F5C' }} />
                <span style={{ fontSize:'.72rem', color:t.accent }}>Active immediately</span>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:8, marginBottom:12 }}>
              {inp('Starts at (optional)', 'starts_at', '', 'date')}
              {inp('Ends at (optional)', 'ends_at', '', 'date')}
            </div>
            {/* Preview */}
            {form.title && (
              <div style={{ marginBottom:12, padding:'10px 14px', borderRadius:8, background:TYPE_BG[form.type]||'#edf6fb', borderLeft:'3px solid '+(TYPE_COLORS[form.type]||'#314F5C') }}>
                <div style={{ fontSize:'.72rem', fontWeight:600, color:TYPE_COLORS[form.type]||'#314F5C', marginBottom:3 }}>{form.title}</div>
                {form.body && <div style={{ fontSize:'.68rem', color:t.mut, lineHeight:1.5 }}>{form.body}</div>}
                {form.country_code && <div style={{ fontSize:'.6rem', color:t.mut, marginTop:3 }}>Showing to: {form.country_code} users only</div>}
              </div>
            )}
            <Btn onClick={handleSave} disabled={saving || !form.title.trim() || !form.body.trim()}>
              {saving ? 'Saving…' : (form.id ? 'Update notice' : 'Publish notice')}
            </Btn>
          </>
        )}
      </Card>

      {/* Notices list */}
      <Card>
        <CardTitle>All notices ({notices.length})</CardTitle>
        {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div>
        : notices.length === 0 ? <Insight type="info">No notices yet. Create one above — it'll appear as a banner in the app.</Insight>
        : notices.map(n => {
          const isExpired = n.ends_at && new Date(n.ends_at) < new Date();
          const notStarted = n.starts_at && new Date(n.starts_at) > new Date();
          const liveStatus = n.active && !isExpired && !notStarted;
          return (
            <div key={n.id} style={{ border:'1px solid '+(liveStatus ? TYPE_COLORS[n.type]+'33' : '#edf6fb'), borderRadius:9, padding:'12px 14px', marginBottom:8, opacity: n.active?1:0.6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <span style={{ fontSize:'.75rem', fontWeight:600, color:t.tx }}>{n.title}</span>
                    <span style={{ fontSize:'.59rem', padding:'1px 6px', borderRadius:5, background:liveStatus?TYPE_COLORS[n.type]+'18':'#f5fafc', color:liveStatus?TYPE_COLORS[n.type]:'#9eb8c8', fontWeight:600 }}>
                      {isExpired?'Expired':notStarted?'Scheduled':n.active?'Live':'Paused'}
                    </span>
                    {n.country_code && <span style={{ fontSize:'.59rem', padding:'1px 5px', borderRadius:4, background:'#edf6fb', color:t.accent }}>{n.country_code}</span>}
                  </div>
                  <div style={{ fontSize:'.68rem', color:t.mut, lineHeight:1.4 }}>{n.body}</div>
                  {(n.starts_at || n.ends_at) && (
                    <div style={{ fontSize:'.6rem', color:t.mut, marginTop:4 }}>
                      {n.starts_at && 'From ' + new Date(n.starts_at).toLocaleDateString()}
                      {n.starts_at && n.ends_at && ' · '}
                      {n.ends_at && 'Until ' + new Date(n.ends_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', gap:5, flexShrink:0, marginLeft:10 }}>
                  <button onClick={() => handleToggle(n)} style={{ padding:'3px 9px', background:'#edf6fb', color:t.accent, border:'none', borderRadius:5, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>
                    {n.active ? 'Pause' : 'Activate'}
                  </button>
                  <button onClick={() => editNotice(n)} style={{ padding:'3px 9px', background:'#edf6fb', color:t.accent, border:'none', borderRadius:5, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>Edit</button>
                  <button onClick={() => handleDelete(n.id)} style={{ padding:'3px 9px', background:'#fef0ef', color:'#c0392b', border:'none', borderRadius:5, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>×</button>
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default function Notices(props) {
  if (props && props.embed) return <NoticesBody {...props} />;
  return <ConsoleShell title="Notices"><NoticesBody {...props} /></ConsoleShell>;
}
